import { useCallback } from 'react'

import { OnboardingResponseStatus } from 'clickNgo/configs/service/onboarding'
import { ContactWorkflowStatus, IContact } from 'clickNgo/entities/contact'
import { TContactGeneralForm } from 'clickNgo/entities/contact'
import { LoadingState } from 'clickNgo/entities/contact'
import { updateContact } from 'clickNgo/entities/contact/api'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { useAppSource } from 'clickNgo/hooks/utils/useAppSource'
import {
  resetAllModifiedDataByKey,
  useGetFormData,
  useGetFormDataField,
} from 'cng-common/features/form'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'

import { ContactEditorFormKeys } from '../../../../configs'
import {
  ContactEditorSettingFormFields,
  TContactEditorData,
  TContactEditorHandles,
  TContactEditorUpdateContextProps,
  THandlerResult,
} from '../../../../models'
import { IOnApplyProps } from '../../../contactEditableContainer'

type TParams = Pick<TContactEditorData, 'contact'> &
  Pick<TContactEditorHandles, 'onContactUpdate'> &
  TContactEditorUpdateContextProps

export function useContactAdditionalInfoUpdate({
  contact,
  source,
  requestPartnerId,
  onContactUpdate,
}: TParams) {
  const dispatch = useAppDispatch()
  const appSource = useAppSource(source || contact.source)

  const [actualData, updateActualData] = useGetFormDataField<TContactEditorData>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.ActualData
  )
  const [, setErrorMessages] = useGetFormDataField<string[]>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.ErrorMessages
  )
  const [, setIsLoading] = useGetFormDataField<LoadingState>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.IsLoading
  )
  const [additionalFormData] = useGetFormData<TContactGeneralForm>(ContactEditorFormKeys.additional)
  const modifiedValues = additionalFormData.modifiedFields
  const hasModifiedValues = modifiedValues.length > 0

  const onApplyHandler = useCallback(
    async ({ workflowStatus }: IOnApplyProps): Promise<THandlerResult> => {
      if (hasModifiedValues && additionalFormData.isValid) {
        const draftContactId =
          workflowStatus === ContactWorkflowStatus.Draft
            ? contact.newContactId || contact.contactId
            : contact.contactId
        const newContact = {
          ...contact,
          ...additionalFormData.formData,
          contactId: draftContactId,
          partnerId: requestPartnerId || contact.partnerId,
          modifiedValues,
          workflowStatus: ContactWorkflowStatus.New,
          source: appSource,
        }

        if (workflowStatus) {
          newContact.workflowStatus = workflowStatus
        }

        setIsLoading({ isLoading: true, message: 'Updating contact...' })
        const response = await updateContact(newContact, { forceRoles: true })

        if (response) {
          switch (response.status) {
            case OnboardingResponseStatus.Fail: {
              setIsLoading({ isLoading: false, message: '' })
              setErrorMessages(getJSendErrorMessages(response))
              return {
                hasError: true,
              }
            }
            case OnboardingResponseStatus.Error: {
              setIsLoading({ isLoading: false, message: '' })
              setErrorMessages(response.message ? [response.message] : [])
              return {
                hasError: true,
              }
            }
            case OnboardingResponseStatus.Success: {
              const updatedContact: IContact = {
                ...newContact,
                ...response.data,
                workflowStatus: response.data.workflowStatus || ContactWorkflowStatus.None,
                modifiedValues:
                  response.data.modifiedValues?.split(',') || newContact.modifiedValues,
              }

              await onContactUpdate(updatedContact)

              updateActualData({
                ...actualData,
                contact: updatedContact,
              })
            }
          }

          setIsLoading({ isLoading: false, message: '' })
          return {
            hasError: false,
          }
        } else {
          setErrorMessages(['Something went wrong!'])
          setIsLoading({ isLoading: false, message: '' })
          return {
            hasError: true,
          }
        }
      }
    },
    [
      actualData,
      additionalFormData.formData,
      additionalFormData.isValid,
      contact,
      hasModifiedValues,
      modifiedValues,
      onContactUpdate,
      setErrorMessages,
      setIsLoading,
      appSource,
      requestPartnerId,
      updateActualData,
    ]
  )

  const onCancelHandle = useCallback<() => Promise<THandlerResult>>(async () => {
    dispatch(resetAllModifiedDataByKey(ContactEditorFormKeys.additional))
  }, [dispatch])

  return { onApplyHandler, onCancelHandle, hasModifiedValues }
}
